/**
 * AI briefs - thin typed client over the four routes of docs/plans/crm-brief-plan.md B5
 * (backend/src/routes/crmBrief.js), in the layout of recruit/api.ts. The list rows do NOT come
 * from here: a brief rides the worklist and work-queue responses, so there is no poll in this file.
 */
import api from '../../services/api';
import type {
  BriefFeedbackRequest,
  BriefFeedbackResponse,
  BriefHealth,
  BriefRefreshResponse,
  BriefResponse,
} from './types';

const contactBase = (contactId: string): string => `/api/crm/contacts/${encodeURIComponent(contactId)}/brief`;

/** The plain sentence to show for a failed call. Never a raw status line or stack. */
export function briefErrorMessage(e: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const res = (e as any)?.response;
  const data = res?.data;
  if (data && typeof data.error === 'string' && data.error.trim()) return data.error;
  if (res?.status === 429) return 'A re-brief was just requested. Try again in a minute.';
  if (res?.status === 503) return 'AI briefs are turned off right now.';
  return fallback;
}

/** True when the server answered 503 BRIEF_DISABLED (or is not serving briefs at all). */
export function isBriefDisabled(e: unknown): boolean {
  const res = (e as any)?.response;
  if (res?.status !== 503) return false;
  const code = res?.data?.code ?? res?.data?.reason ?? res?.data?.error;
  return code == null || code === 'BRIEF_DISABLED';
}

/** The lead's current brief. `{ brief: null }` when the server has none (a 404 reads as no brief). */
export async function getBrief(contactId: string): Promise<BriefResponse> {
  try {
    const res = await api.get(contactBase(contactId));
    const data = res.data || {};
    return { brief: data.brief ?? null, state: data.state === 'off' ? 'off' : 'enabled' };
  } catch (e) {
    if ((e as any)?.response?.status === 404) return { brief: null, state: 'enabled' };
    throw e;
  }
}

/** Ask for a fresh brief now (one per lead per minute per user; the server answers 202). */
export async function refreshBrief(contactId: string): Promise<BriefRefreshResponse> {
  const res = await api.post(`${contactBase(contactId)}/refresh`);
  return { queued: res.data?.queued !== false };
}

/** File one thumbs vote (and an optional note) against a run. Append-only on the server. */
export async function sendBriefFeedback(contactId: string, body: BriefFeedbackRequest): Promise<BriefFeedbackResponse> {
  const res = await api.post(`${contactBase(contactId)}/feedback`, body);
  return res.data as BriefFeedbackResponse;
}

/** Admin-only health read. */
export async function getBriefHealth(): Promise<BriefHealth> {
  const res = await api.get('/api/crm/brief/health');
  return res.data as BriefHealth;
}
